// cspell:ignore beforeinstallprompt appinstalled
import { Download, Info, X } from 'lucide-react';
import { Fragment, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import type { BeforeInstallPromptEvent } from '@/stores/pwa-install-store';
import { usePwaInstallStore } from '@/stores/pwa-install-store';

type InstallPromptVariant = 'banner' | 'settings' | 'sidebar';

function isStandaloneDisplay(): boolean {
  const browser = globalThis as typeof globalThis & { window?: Window };
  const mediaQueryList = browser.window?.matchMedia?.('(display-mode: standalone)');
  const navigatorStandalone =
    (browser.navigator as Navigator & { standalone?: boolean })?.standalone ?? false;

  return Boolean(mediaQueryList?.matches) || navigatorStandalone;
}

function getPlatform(): 'ios' | 'android' | 'desktop' {
  if (typeof navigator === 'undefined') {
    return 'desktop';
  }

  const userAgent = navigator.userAgent ?? '';
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return 'ios';
  }
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  return 'desktop';
}

export function InstallPrompt({ variant = 'banner' }: { readonly variant?: InstallPromptVariant }) {
  const { t } = useTranslation('common');
  const deferredPrompt = usePwaInstallStore((state) => state.deferredPrompt);
  const shouldShowPrompt = usePwaInstallStore((state) => state.shouldShowPrompt);
  const isInstalled = usePwaInstallStore((state) => state.isInstalled);
  const setDeferredPrompt = usePwaInstallStore((state) => state.setDeferredPrompt);
  const setInstalled = usePwaInstallStore((state) => state.setInstalled);
  const hidePrompt = usePwaInstallStore((state) => state.hidePrompt);

  const [isInstructionOpen, setInstructionOpen] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'desktop'>('desktop');

  useEffect(() => {
    setPlatform(getPlatform());

    const browser = globalThis as typeof globalThis & { window?: Window };
    const currentWindow = browser.window;
    if (!currentWindow) {
      return;
    }

    if (!isInstalled && isStandaloneDisplay()) {
      setInstalled(true);
    }

    const handleBeforeInstallPrompt = (event: BeforeInstallPromptEvent) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleAppInstalled = () => {
      if (!isInstalled) {
        setInstalled(true);
      }
      setDeferredPrompt(undefined);
    };

    const displayModeQuery = currentWindow.matchMedia?.('(display-mode: standalone)');
    const handleDisplayModeChange = (event: MediaQueryListEvent) => {
      if (event.matches && !isInstalled) {
        setInstalled(true);
      }
    };
    const legacyListener = handleDisplayModeChange as (
      this: MediaQueryList,
      ev: MediaQueryListEvent,
    ) => void;

    currentWindow.addEventListener(
      'beforeinstallprompt',
      handleBeforeInstallPrompt as EventListener,
    );
    currentWindow.addEventListener('appinstalled', handleAppInstalled);

    displayModeQuery?.addEventListener?.('change', handleDisplayModeChange);
    // eslint-disable-next-line sonarjs/deprecation -- Safari < 14 still relies on the legacy listener API.
    displayModeQuery?.addListener?.(legacyListener);

    return () => {
      currentWindow.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt as EventListener,
      );
      currentWindow.removeEventListener('appinstalled', handleAppInstalled);
      displayModeQuery?.removeEventListener?.('change', handleDisplayModeChange);
      // eslint-disable-next-line sonarjs/deprecation -- Safari < 14 still relies on the legacy listener API.
      displayModeQuery?.removeListener?.(legacyListener);
    };
  }, [isInstalled, setDeferredPrompt, setInstalled]);

  const hasNativePrompt = Boolean(deferredPrompt);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      setInstructionOpen(true);
      return;
    }

    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;

      if (choice.outcome === 'accepted') {
        setInstalled(true);
      } else {
        hidePrompt({ permanent: true });
      }
    } catch (error) {
      console.warn('Unable to present PWA install prompt:', error);
      hidePrompt();
      setInstructionOpen(true);
    } finally {
      setDeferredPrompt(undefined);
    }
  };

  const handleDismiss = () => {
    hidePrompt({ permanent: true });
    setDeferredPrompt(undefined);
  };

  const instructionSteps = useMemo(() => {
    const baseKey = `pwa.instructions.${platform}` as const;
    return [t(`${baseKey}.step1`), t(`${baseKey}.step2`), t(`${baseKey}.step3`)].filter(Boolean);
  }, [platform, t]);

  const instructionContent = (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{t('pwa.instructions.title')}</DialogTitle>
        <DialogDescription>{t('pwa.instructions.description')}</DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Info className="h-4 w-4 shrink-0" />
          <span>
            {t('pwa.instructions.platformMessage', {
              platform: t(`pwa.instructions.${platform}.name`),
            })}
          </span>
        </div>
        <ol className="list-decimal space-y-2 pl-5 text-sm">
          {instructionSteps.map((step, index) => (
            <li key={index} className="leading-snug">
              {step}
            </li>
          ))}
        </ol>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => setInstructionOpen(false)}>
          {t('close')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );

  const renderInstructionDialog = (trigger?: ReactNode) => (
    <Dialog open={isInstructionOpen} onOpenChange={setInstructionOpen}>
      {trigger}
      {instructionContent}
    </Dialog>
  );

  if (isInstalled) {
    return <></>;
  }

  if (variant === 'settings') {
    if (isInstalled) {
      return (
        <div className="rounded-md border border-dashed border-muted px-4 py-3 text-sm text-muted-foreground">
          {t('settings.install.installed')}
        </div>
      );
    }

    const instructionsTrigger = (
      <DialogTrigger asChild>
        <Button variant="link" className="px-0 text-sm font-medium">
          {t('settings.install.instructions')}
        </Button>
      </DialogTrigger>
    );

    return (
      <Fragment>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Button
            className="gap-2 sm:w-auto"
            onClick={() => void handleInstallClick()}
            disabled={Boolean(isInstalled)}
          >
            <Download className="h-4 w-4" />
            {t('pwa.install')}
          </Button>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Info className="h-4 w-4 shrink-0" />
            <span>{t('settings.install.help')}</span>
          </div>
        </div>
        <div>{renderInstructionDialog(instructionsTrigger)}</div>
      </Fragment>
    );
  }

  if (variant === 'sidebar') {
    if (hasNativePrompt) {
      return (
        <SidebarMenuItem>
          <SidebarMenuButton
            className="cursor-pointer"
            onClick={() => void handleInstallClick()}
            tooltip={t('pwa.install')}
          >
            <Download className="h-4 w-4 shrink-0" />
            <span>{t('pwa.install')}</span>
          </SidebarMenuButton>
          {renderInstructionDialog()}
        </SidebarMenuItem>
      );
    }

    return (
      <SidebarMenuItem>
        {renderInstructionDialog(
          <DialogTrigger asChild>
            <SidebarMenuButton className="cursor-pointer" tooltip={t('pwa.install')}>
              <Download className="h-4 w-4 shrink-0" />
              <span>{t('pwa.install')}</span>
            </SidebarMenuButton>
          </DialogTrigger>,
        )}
      </SidebarMenuItem>
    );
  }

  if (!hasNativePrompt || !shouldShowPrompt) {
    return (
      <>
        <Button size="sm" className="gap-1" onClick={() => setInstructionOpen(true)}>
          <Download className="h-3.5 w-3.5" />
          {t('pwa.install')}
        </Button>
        {renderInstructionDialog()}
      </>
    );
  }

  return (
    <Fragment>
      <div className="flex items-center gap-1">
        <Button size="sm" className="gap-1" onClick={() => void handleInstallClick()}>
          <Download className="h-3.5 w-3.5" />
          {t('pwa.install')}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          aria-label={t('pwa.dismiss')}
          onClick={handleDismiss}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      {renderInstructionDialog()}
    </Fragment>
  );
}
